import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface UserData {
  personalInfo: {
    firstName: string;
    lastName: string;
    fullName: string;
    dateOfBirth: string;
    age: number;
    address: string;
    phone: string;
    email: string;
  };
  documents: any[];
}

interface UserContextType {
  userData: UserData | null;
  saveUserData: (data: UserData) => void;
  isLoading: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Load from localStorage on mount
    const savedData = localStorage.getItem('userData');
    if (savedData) {
      try {
        setUserData(JSON.parse(savedData));
      } catch (e) {
        console.error("Failed to parse user data from localStorage", e);
      }
    }
    setIsLoading(false);
  }, []);

  const saveUserData = (data: UserData) => {
    localStorage.setItem('userData', JSON.stringify(data));
    setUserData(data);
  };

  return (
    <UserContext.Provider value={{ userData, saveUserData, isLoading }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
